import {useState} from "react";
import {Autocomplete, Box, Button, Chip, Divider, MenuItem, Stack, Switch, TextField, Typography, FormControlLabel} from "@mui/material";
import {Navigate, useParams} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles, hintColor} from "../../../AppStyles.ts";
import type {SearchHit, SearchPointer, SearchResults} from "../../../sdo/SearchResult.ts";

// --- Filter options ---
// TODO: Replace with API call to load available types for the current network

const RESOURCE_TYPE_OPTIONS = [
    {value: "ALL", label: "All"},
    {value: "PAGES", label: "Pages"},
    {value: "FILES", label: "Files"},
    {value: "RECORDS", label: "Records"},
];

const INTEL_TYPE_OPTIONS = [
    {value: "ALL", label: "All"},
    {value: "PERSON", label: "Person"},
    {value: "ORGANIZATION", label: "Organization"},
    {value: "PLACE", label: "Place"},
    {value: "EVENT", label: "Event"},
    {value: "OBJECT", label: "Object"},
];

const PAGE_SIZE_OPTIONS = [
    {value: -1, label: "All"},
    {value: 25, label: "25"},
    {value: 50, label: "50"},
    {value: 100, label: "100"},
    {value: 250, label: "250"},
    {value: 500, label: "500"},
    {value: 1000, label: "1000"},
];

// --- Sample data ---

const ALL_SAMPLE_HITS: SearchHit[] = [
    {pointer: {uid: "p-1", display: "John Marcus Riley", uniqueKey: "intel-person-001", profileType: "INTEL", typeKey: "PERSON", definitionKey: "Individual", matchHash: "a1b2c3"}, score: 0.95},
    {pointer: {uid: "p-2", display: "Sentinel Group Holdings", uniqueKey: "intel-org-002", profileType: "INTEL", typeKey: "ORGANIZATION", definitionKey: "Corporation", matchHash: "d4e5f6"}, score: 0.88},
    {pointer: {uid: "p-3", display: "Operation Northwind - Briefing Document", uniqueKey: "res-pages-003", profileType: "RESOURCE", typeKey: "PAGES", definitionKey: "Intelligence Report", matchHash: "g7h8i9"}, score: 0.82},
    {pointer: {uid: "p-4", display: "Safe House Location - Berlin", uniqueKey: "intel-place-004", profileType: "INTEL", typeKey: "PLACE", definitionKey: "Location", matchHash: "j0k1l2"}, score: 0.79},
    {pointer: {uid: "p-5", display: "Financial Transfer Records Q3", uniqueKey: "res-records-005", profileType: "RESOURCE", typeKey: "RECORDS", definitionKey: "Transaction Log", matchHash: "m3n4o5"}, score: 0.75},
    {pointer: {uid: "p-6", display: "Surveillance Imagery - Port Authority", uniqueKey: "res-files-006", profileType: "RESOURCE", typeKey: "FILES", definitionKey: "Image Archive", matchHash: "p6q7r8"}, score: 0.71},
    {pointer: {uid: "p-7", display: "Annual Strategy Conference 2025", uniqueKey: "intel-event-007", profileType: "INTEL", typeKey: "EVENT", definitionKey: "Gathering", matchHash: "s9t0u1"}, score: 0.68},
    {pointer: {uid: "p-8", display: "Encrypted Communications Device", uniqueKey: "intel-obj-008", profileType: "INTEL", typeKey: "OBJECT", definitionKey: "Equipment", matchHash: "v2w3x4"}, score: 0.64},
    {pointer: {uid: "p-9", display: "Maria Elena Vasquez", uniqueKey: "intel-person-009", profileType: "INTEL", typeKey: "PERSON", definitionKey: "Individual", matchHash: "y5z6a7"}, score: 0.61},
    {pointer: {uid: "p-10", display: "Meridian Logistics International", uniqueKey: "intel-org-010", profileType: "INTEL", typeKey: "ORGANIZATION", definitionKey: "Corporation", matchHash: "b8c9d0"}, score: 0.58},
    {pointer: {uid: "p-11", display: "Port of Rotterdam Cargo Manifest", uniqueKey: "res-records-011", profileType: "RESOURCE", typeKey: "RECORDS", definitionKey: "Shipping Document", matchHash: "e1f2g3"}, score: 0.55},
    {pointer: {uid: "p-12", display: "Warehouse District - Istanbul", uniqueKey: "intel-place-012", profileType: "INTEL", typeKey: "PLACE", definitionKey: "Location", matchHash: "h4i5j6"}, score: 0.52},
    {pointer: {uid: "p-13", display: "Analyst Debrief Notes - March 2026", uniqueKey: "res-pages-013", profileType: "RESOURCE", typeKey: "PAGES", definitionKey: "Intelligence Report", matchHash: "k7l8m9"}, score: 0.49},
    {pointer: {uid: "p-14", display: "Signal Intercept Alpha-7", uniqueKey: "res-files-014", profileType: "RESOURCE", typeKey: "FILES", definitionKey: "Communications Log", matchHash: "n0o1p2"}, score: 0.46},
    {pointer: {uid: "p-15", display: "Regional Summit - Ankara", uniqueKey: "intel-event-015", profileType: "INTEL", typeKey: "EVENT", definitionKey: "Gathering", matchHash: "q3r4s5"}, score: 0.43},
    {pointer: {uid: "p-16", display: "Satellite Phone IMEI Trace", uniqueKey: "intel-obj-016", profileType: "INTEL", typeKey: "OBJECT", definitionKey: "Equipment", matchHash: "t6u7v8"}, score: 0.40},
];

// TODO: Replace with API call to SearchManager.search()
function buildTestResults(terms: string[], requireAll: boolean, page: number, pageSize: number): SearchResults {
    const normalizedTerms = terms.map(t => t.trim().toLowerCase()).filter(t => t.length > 0);

    const filtered = normalizedTerms.length === 0 ? ALL_SAMPLE_HITS : ALL_SAMPLE_HITS.filter(h => {
        const haystack = `${h.pointer.display} ${h.pointer.typeKey} ${h.pointer.definitionKey}`.toLowerCase();
        return requireAll
            ? normalizedTerms.every(t => haystack.includes(t))
            : normalizedTerms.some(t => haystack.includes(t));
    });

    const totalMatches = filtered.length;
    const effectivePageSize = pageSize <= 0 ? Math.max(totalMatches, 1) : pageSize;
    const totalPages = effectivePageSize > 0 ? Math.ceil(totalMatches / effectivePageSize) : 1;
    const clampedPage = Math.min(page, totalPages || 1);
    const start = (clampedPage - 1) * effectivePageSize;
    const pageResults = filtered.slice(start, start + effectivePageSize);

    return {
        page: clampedPage,
        pageSize: effectivePageSize,
        totalPages,
        totalMatches,
        searchTime: 42,
        results: pageResults,
    };
}

// --- Form field styles (mirrors fieldSx / selectSx from ResolverUtils) ---

const formFieldSx = {
    backgroundColor: "rgba(9, 25, 32, 0.6)",
    borderRadius: "6px",
    "& .MuiOutlinedInput-root": {
        borderRadius: "6px",
        color: "#f2f5fa",
        fontSize: "0.75rem",
        height: "38px",
        "& fieldset": {border: "1px solid rgba(112, 147, 160, 0.35)"},
        "&:hover fieldset": {border: "1px solid #02cfee"},
        "&.Mui-focused fieldset": {border: "1px solid #38ff82", boxShadow: "0 0 6px rgba(56, 255, 130, 0.2)"},
    },
    "& .MuiInputLabel-root": {color: "rgba(242, 245, 250, 0.55)", fontSize: "0.75rem"},
    "& .MuiInputLabel-root.Mui-focused": {color: "#38ff82"},
};

const formSelectSx = {
    ...formFieldSx,
    flex: 1,
    minWidth: 160,
    "& .MuiSelect-select": {color: "#f2f5fa", fontSize: "0.75rem", textAlign: "left" as const},
    "& .MuiSelect-icon": {color: "#7093a0"},
};

// --- Result row styles (vertically stacked, fixed-width, centered list) ---

const resultsColumnSx = {
    maxWidth: 720,
    width: "100%",
    margin: "0 auto",
};

const resultsFoundTitleSx = {
    fontSize: "0.95rem",
    fontWeight: 600,
    color: "#FFFFFF",
    textAlign: "center" as const,
    padding: "8px 0 14px",
    letterSpacing: 0.3,
};

const resultRowSx = {
    borderBottom: "1px solid rgba(63, 70, 79, 0.6)",
    padding: "14px 16px",
    "&:last-of-type": { borderBottom: "none" },
};

const rowTitleSx = {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: "#FFFFFF",
    textAlign: "left" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
};

const rowMetaSx = {
    fontSize: "0.7rem",
    fontWeight: 400,
    color: "#a8b8c0",
    textAlign: "left" as const,
};

const rowMetaDotSx = {
    fontSize: "0.7rem",
    fontWeight: 400,
    color: "#a8b8c0",
    opacity: 0.5,
};

// --- Result container ---

const resultContainerSx = {
    border: "1px solid #3F464F",
    borderRadius: "10px",
    backgroundColor: "rgba(9, 25, 32, 0.4)",
    minHeight: 300,
    width: "100%",
    padding: "12px",
};

// --- Pagination styles ---

const pageButtonSx = {
    fontSize: "0.7rem",
    color: "#a8b8c0",
    cursor: "pointer",
    padding: "4px 10px",
    borderRadius: "6px",
    border: "1px solid rgba(112, 147, 160, 0.35)",
    userSelect: "none" as const,
    "&:hover": {
        color: "#02cfee",
        borderColor: "#02cfee",
        backgroundColor: "rgba(2, 207, 238, 0.08)",
    },
};

const pageNumberSx = (active: boolean) => ({
    fontSize: "0.7rem",
    fontWeight: active ? 700 : 400,
    color: active ? "#091920" : "#a8b8c0",
    backgroundColor: active ? "#02cfee" : "transparent",
    borderRadius: "6px",
    padding: "4px 8px",
    cursor: "pointer",
    minWidth: "28px",
    textAlign: "center" as const,
    userSelect: "none" as const,
    "&:hover": active ? {} : {
        color: "#02cfee",
        backgroundColor: "rgba(2, 207, 238, 0.08)",
    },
});

// --- Helpers ---

function profileIcon(pointer: SearchPointer): string {
    if (pointer.profileType === "RESOURCE") return `/images/resource_${pointer.typeKey.toLowerCase()}.svg`;
    return `/images/intel_${pointer.typeKey.toLowerCase()}.svg`;
}

function profileTypeLabel(pointer: SearchPointer): string {
    const typeDisplay = pointer.typeKey.charAt(0) + pointer.typeKey.slice(1).toLowerCase();
    if (pointer.profileType === "INTEL") return `Intelligence: ${typeDisplay}`;
    if (pointer.profileType === "RESOURCE") return `Resource: ${typeDisplay}`;
    return `Association: ${typeDisplay}`;
}

// --- Result row ---

function SearchResultRow({hit}: { hit: SearchHit }) {
    const pointer = hit.pointer;
    return (
        <Box sx={resultRowSx}>
            <Stack direction="row" spacing={2} alignItems="center">
                <Box
                    component="img"
                    src={profileIcon(pointer)}
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = "/images/pages_white.svg"; }}
                    sx={{width: 40, height: 40, flexShrink: 0, opacity: 0.85}}
                />
                <Stack spacing={0.4} sx={{minWidth: 0, flex: 1}}>
                    <Typography sx={rowTitleSx} title={pointer.display}>{pointer.display}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={rowMetaSx}>{profileTypeLabel(pointer)}</Typography>
                        <Typography sx={rowMetaDotSx}>·</Typography>
                        <Typography sx={rowMetaSx}>{pointer.definitionKey}</Typography>
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
}

// --- Main component ---

function UXSearch() {
    const {networkUID} = useParams();

    const [searchTerms, setSearchTerms] = useState<string[]>([]);
    const [requireAll, setRequireAll] = useState(true);
    const [resourceType, setResourceType] = useState("ALL");
    const [intelType, setIntelType] = useState("ALL");
    const [pageSize, setPageSize] = useState(50);
    const [results, setResults] = useState<SearchResults | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searching, setSearching] = useState(false);
    const [lastTerms, setLastTerms] = useState<string[]>([]);
    const [lastRequireAll, setLastRequireAll] = useState(true);

    function handleTermsChange(newTerms: string[]) {
        // De-duplicate (case-insensitive) and trim — mirrors InsightEditor tags handling
        const seen = new Set<string>();
        const cleaned: string[] = [];
        for (const raw of newTerms) {
            const term = raw.trim();
            if (!term) continue;
            const key = term.toLowerCase();
            if (seen.has(key)) continue;
            seen.add(key);
            cleaned.push(term);
        }
        setSearchTerms(cleaned);
    }

    function doSearch(terms: string[], all: boolean, page: number, ps: number) {
        if (terms.length === 0) return;
        setSearching(true);
        // TODO: Replace with API call to SearchManager.search(request)
        const data = buildTestResults(terms, all, page, ps);
        setResults(data);
        setCurrentPage(data.page);
        setSearching(false);
    }

    function handleSearch() {
        if (searchTerms.length === 0) return;
        setLastTerms(searchTerms);
        setLastRequireAll(requireAll);
        setCurrentPage(1);
        doSearch(searchTerms, requireAll, 1, pageSize);
    }

    function handlePageChange(page: number) {
        doSearch(lastTerms, lastRequireAll, page, pageSize);
    }

    const showingLabel = (): string => {
        if (!results || results.totalMatches === 0) return "";
        const from = (results.page === 1) ? 1 : ((results.page - 1) * results.pageSize) + 1;
        const to = Math.min(results.page * results.pageSize, results.totalMatches);
        return `Showing ${from}-${to} of ${results.totalMatches}`;
    };

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {!appSession.activeSession() ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Search"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Search", linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack sx={appStyles.sectionBox} spacing={0} width="100%" alignItems="center">
                        {/* Page title */}
                        <Typography sx={{...appStyles.panelSectionTitle, padding: "16px 0 4px"}}>
                            Search Network
                        </Typography>

                        {/* Search form panel */}
                        <Box sx={{width: "100%", maxWidth: 600, padding: "19px 27px 27px"}}>
                            <Stack spacing={1.5} alignItems="center">
                                {/* Row 1: Multi-term search field + All Terms toggle */}
                                <Stack direction="row" spacing={1.5} alignItems="flex-start" width="100%">
                                    <Autocomplete
                                        multiple
                                        freeSolo
                                        options={[]}
                                        value={searchTerms}
                                        onChange={(_e, newValue) => handleTermsChange(newValue as string[])}
                                        sx={{flex: 1}}
                                        renderTags={(value: readonly string[], getTagProps) =>
                                            value.map((term, index) => {
                                                const {key, ...tagProps} = getTagProps({index});
                                                return (
                                                    <Chip
                                                        key={key}
                                                        label={term}
                                                        size="small"
                                                        {...tagProps}
                                                        sx={{
                                                            backgroundColor: "rgba(2, 207, 238, 0.12)",
                                                            color: "#02cfee",
                                                            border: "1px solid rgba(2, 207, 238, 0.35)",
                                                            fontSize: "0.72rem",
                                                            height: "22px",
                                                            "& .MuiChip-deleteIcon": {
                                                                color: "rgba(2, 207, 238, 0.7)",
                                                                "&:hover": {color: hintColor},
                                                            },
                                                        }}
                                                    />
                                                );
                                            })
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Search Terms"
                                                size="small"
                                                placeholder="Type a term and press Enter"
                                                sx={{
                                                    ...formFieldSx,
                                                    "& .MuiOutlinedInput-root": {
                                                        ...(formFieldSx as Record<string, unknown>)["& .MuiOutlinedInput-root"] as object,
                                                        height: "auto",
                                                        minHeight: "38px",
                                                        padding: "4px 8px",
                                                        flexWrap: "wrap",
                                                        gap: "4px",
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={requireAll}
                                                onChange={e => setRequireAll(e.target.checked)}
                                                size="small"
                                                sx={{
                                                    "& .MuiSwitch-switchBase.Mui-checked": {color: "#02cfee"},
                                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {backgroundColor: "#02cfee"},
                                                }}
                                            />
                                        }
                                        label={<Typography sx={{fontSize: "0.7rem", color: "#a8b8c0", whiteSpace: "nowrap"}}>All Terms</Typography>}
                                        labelPlacement="start"
                                        sx={{ml: 0, mr: 0, mt: "6px"}}
                                    />
                                </Stack>

                                {/* Row 2: Resource + Intel type selectors */}
                                <Stack direction="row" spacing={1.5} width="100%">
                                    <TextField
                                        select size="small" label="Resource Profiles"
                                        value={resourceType}
                                        onChange={e => setResourceType(e.target.value)}
                                        sx={formSelectSx}
                                    >
                                        {RESOURCE_TYPE_OPTIONS.map(o => (
                                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        select size="small" label="Intelligence Profiles"
                                        value={intelType}
                                        onChange={e => setIntelType(e.target.value)}
                                        sx={formSelectSx}
                                    >
                                        {INTEL_TYPE_OPTIONS.map(o => (
                                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>

                                {/* Row 3: Search button (contained, matches "New Mark" style) */}
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleSearch}
                                    sx={appStyles.panelActionButton}
                                >
                                    Search
                                </Button>
                            </Stack>
                        </Box>

                        <Divider sx={{borderColor: "#3F464F", width: "100%"}} />

                        {/* Results section */}
                        <Box sx={{width: "100%", padding: "12px 16px"}}>
                            <Box sx={resultContainerSx}>
                                {/* Top bar: results per page selector */}
                                <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{padding: "4px 4px 8px"}}>
                                    <TextField
                                        select size="small" label="Results / Page"
                                        value={pageSize}
                                        onChange={e => {
                                            const ps = Number(e.target.value);
                                            setPageSize(ps);
                                            if (lastTerms.length > 0) doSearch(lastTerms, lastRequireAll, 1, ps);
                                        }}
                                        sx={{...formSelectSx, flex: "none", minWidth: 120}}
                                    >
                                        {PAGE_SIZE_OPTIONS.map(o => (
                                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                        ))}
                                    </TextField>
                                </Stack>

                                <Divider sx={{borderColor: "#3F464F", mb: 1.5}} />

                                {/* Result rows — centered, fixed-width, vertically stacked */}
                                {results && results.results.length > 0 ? (
                                    <Box sx={resultsColumnSx}>
                                        <Typography sx={resultsFoundTitleSx}>
                                            {results.totalMatches.toLocaleString()} search {results.totalMatches === 1 ? "result" : "results"} found
                                        </Typography>
                                        <Stack direction="column" spacing={0}>
                                            {results.results.map(hit => (
                                                <SearchResultRow key={hit.pointer.uid} hit={hit} />
                                            ))}
                                        </Stack>
                                    </Box>
                                ) : results && results.totalMatches === 0 ? (
                                    <Stack alignItems="center" justifyContent="center" sx={{minHeight: 200}}>
                                        <Typography sx={{color: "#a8b8c0", fontSize: "0.85rem"}}>
                                            No results matched your search criteria.
                                        </Typography>
                                    </Stack>
                                ) : null}

                                {/* Pagination footer — always visible when results exist */}
                                {results && (
                                    <>
                                        <Divider sx={{borderColor: "#3F464F", mt: 1.5}} />
                                        <Typography sx={{...appStyles.showCountText, textAlign: "center", padding: "10px 0 0"}}>
                                            {searching ? "Searching..." : showingLabel()}
                                        </Typography>
                                        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{padding: "12px 0 4px"}}>
                                            <Typography
                                                sx={{...pageButtonSx, opacity: currentPage <= 1 ? 0.35 : 1, pointerEvents: currentPage <= 1 ? "none" : "auto"}}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            >
                                                Previous
                                            </Typography>
                                            <Stack direction="row" spacing={0.5}>
                                                {Array.from({length: Math.max(results.totalPages, 1)}, (_, i) => i + 1).map(p => (
                                                    <Typography
                                                        key={p}
                                                        sx={pageNumberSx(p === currentPage)}
                                                        onClick={() => handlePageChange(p)}
                                                    >
                                                        {p}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                            <Typography
                                                sx={{...pageButtonSx, opacity: currentPage >= Math.max(results.totalPages, 1) ? 0.35 : 1, pointerEvents: currentPage >= Math.max(results.totalPages, 1) ? "none" : "auto"}}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            >
                                                Next
                                            </Typography>
                                        </Stack>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXSearch;
